import { useEffect, useState } from 'react';

/**
 * useMediaQuery — small subscription hook around window.matchMedia.
 * Returns the current match state and updates on change. SSR-safe (returns
 * `false` during the first render server-side).
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * Tailwind-aligned breakpoints. The mobile breakpoint is 640px to match the
 * dashboard's stacked-card cutoff (DR-4 / Pass 6A).
 */
export function useResponsive() {
  const isMobile = useMediaQuery('(max-width: 639.98px)');
  const isTablet = useMediaQuery('(min-width: 640px) and (max-width: 1023.98px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  return { isMobile, isTablet, isDesktop };
}
